import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Mic, X, Paperclip, Image, FileText, File } from 'lucide-react';
import { VoiceRecorder } from './VoiceRecorder';
import { RichTextEditor } from '../editor/RichTextEditor';
import { cn } from '../../lib/utils';

interface AttachedFile {
  file: File;
  preview?: string;
}

interface CommentInputProps {
  onSubmit: (content: string | null, voiceBlob?: Blob, voiceDuration?: number, files?: File[]) => void;
  placeholder?: string;
  disabled?: boolean;
  projectId?: string;
}

export function CommentInput({
  onSubmit,
  placeholder = 'Write a comment...',
  disabled,
  projectId,
}: CommentInputProps) {
  const [content, setContent] = useState('');
  const [showVoice, setShowVoice] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleTextSubmit = async () => {
    const plainText = content.replace(/<[^>]*>/g, '').trim();
    if (!plainText && attachedFiles.length === 0) return;
    setIsSubmitting(true);
    try {
      const files = attachedFiles.map(f => f.file);
      // Pass the HTML content for mention processing
      await onSubmit(content || null, undefined, undefined, files.length > 0 ? files : undefined);
      setContent('');
      setAttachedFiles([]);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVoiceSubmit = async (blob: Blob, duration: number) => {
    setIsSubmitting(true);
    try {
      await onSubmit(null, blob, duration);
      setShowVoice(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleTextSubmit();
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const newAttachments: AttachedFile[] = files.map(file => {
      const attachment: AttachedFile = { file };
      if (file.type.startsWith('image/')) {
        attachment.preview = URL.createObjectURL(file);
      }
      return attachment;
    });
    setAttachedFiles(prev => [...prev, ...newAttachments]);
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = (index: number) => {
    setAttachedFiles(prev => {
      const newFiles = [...prev];
      if (newFiles[index].preview) {
        URL.revokeObjectURL(newFiles[index].preview!);
      }
      newFiles.splice(index, 1);
      return newFiles;
    });
  };

  const getFileIcon = (file: File) => {
    if (file.type.startsWith('image/')) return Image;
    if (file.type.includes('pdf') || file.type.includes('document')) return FileText;
    return File;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="space-y-3">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*,.pdf,.doc,.docx,.txt"
        onChange={handleFileSelect}
        className="hidden"
      />

      <AnimatePresence mode="wait">
        {showVoice ? (
          <motion.div
            key="voice"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <div className="flex items-start gap-2">
              <div className="flex-1">
                <VoiceRecorder
                  onRecordingComplete={handleVoiceSubmit}
                  disabled={disabled || isSubmitting}
                />
              </div>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setShowVoice(false)}
                className="p-2 rounded-lg hover:bg-bg-tertiary text-text-muted hover:text-text transition-colors"
              >
                <X className="w-5 h-5" />
              </motion.button>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="text"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-3"
          >
            {/* Attached files preview */}
            {attachedFiles.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {attachedFiles.map((attachment, index) => {
                  const FileIcon = getFileIcon(attachment.file);
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="relative group"
                    >
                      {attachment.preview ? (
                        <div className="w-20 h-20 rounded-lg overflow-hidden border border-border bg-bg-tertiary">
                          <img
                            src={attachment.preview}
                            alt={attachment.file.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : (
                        <div className="w-20 h-20 rounded-lg border border-border bg-bg-tertiary flex flex-col items-center justify-center p-2">
                          <FileIcon className="w-6 h-6 text-text-muted" />
                          <span className="text-[10px] text-text-muted truncate w-full text-center mt-1">
                            {attachment.file.name.split('.').pop()?.toUpperCase()}
                          </span>
                        </div>
                      )}
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => removeFile(index)}
                        className="absolute -top-1.5 -right-1.5 p-1 rounded-full bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </motion.button>
                      <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[9px] px-1 py-0.5 truncate rounded-b-lg">
                        {formatFileSize(attachment.file.size)}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}

            {/* Input row */}
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <RichTextEditor
                  content={content}
                  onChange={setContent}
                  placeholder={projectId ? `${placeholder} Use @ to mention someone` : placeholder}
                  projectId={projectId}
                  editable={!disabled && !isSubmitting}
                  className="min-h-[80px]"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                {/* Attach file button */}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={disabled}
                  title="Attach file"
                  type="button"
                  className={cn(
                    'p-2.5 rounded-xl border border-border',
                    'hover:bg-accent/10 hover:border-accent/30 hover:text-accent',
                    'transition-all duration-200',
                    disabled && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  <Paperclip className="w-5 h-5" />
                </motion.button>

                {/* Voice button */}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowVoice(true)}
                  disabled={disabled}
                  title="Record voice message"
                  type="button"
                  className={cn(
                    'p-2.5 rounded-xl border border-border',
                    'hover:bg-accent/10 hover:border-accent/30 hover:text-accent',
                    'transition-all duration-200',
                    disabled && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  <Mic className="w-5 h-5" />
                </motion.button>

                {/* Send button */}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleTextSubmit}
                  disabled={disabled || (!content.replace(/<[^>]*>/g, '').trim() && attachedFiles.length === 0) || isSubmitting}
                  title="Send comment"
                  type="button"
                  className={cn(
                    'p-2.5 rounded-xl',
                    'bg-accent hover:bg-accent-light text-white',
                    'transition-all duration-200',
                    'disabled:opacity-50 disabled:cursor-not-allowed'
                  )}
                >
                  <Send className="w-5 h-5" />
                </motion.button>
              </div>
            </div>

            {/* Helper text */}
            <p className="text-xs text-text-muted flex items-center gap-2">
              {projectId && <span>Type @ to mention someone</span>}
              {attachedFiles.length > 0 && (
                <>
                  {projectId && <span>•</span>}
                  <span className="text-accent">{attachedFiles.length} file(s) attached</span>
                </>
              )}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
